import React, { useEffect, useRef, useState } from "react";
import {
  useNavigate,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getUserInfo } from "@/api/Api";
import ContinueButton from "@/components/Buttons/ContinueButton";
import {
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import MotivationGoal from "@/components/Onboarding/MotivationGoal";
import FluentGoal from "@/components/Onboarding/FluentGoal";
import StudyHabit from "@/components/Onboarding/StudyHabit";
import NotificationPopUp from "@/components/NotificationPopUp";
import Challenges from "@/components/Onboarding/Challenges";
import PlanLoading from "@/components/Onboarding/PlanLoading";
import PlanLoaded from "@/components/Onboarding/PlanLoaded";
import { getPlatform } from "@/lib/iap";
import LanguageStep from "@/components/Onboarding/LanguageStep";
import Credibility from "@/components/Onboarding/Credibility";
import OnboardingHeader from "@/components/Onboarding/OnboardingHeader";
import FreeTrial from "@/components/Onboarding/FreeTrial";
import NotificationStep from "@/components/Onboarding/NotificationStep";
import SpanishLevelStep from "@/components/Onboarding/SpanishLevelStep";
import {
  LearningReason,
  LanguageLearningChallenge,
  LanguageLevelOnboarding,
} from "@shared_types";
import { useOnboarding } from "@/hooks/useOnboarding";
import "@/TransitionsHorizontal.css";
import "../App.css";
import "../Animations.css";
import { usePostHog } from "posthog-js/react";
import { addMonths } from "date-fns";
import { format } from "date-fns";
import { tokenStorage } from "@/api/tokenStorage";

// Analytics keys for tracking onboarding steps
export enum OnboardingAnalyticsKey {
  LANGUAGE = "language_step",
  MOTIVATION_GOAL = "motivation_goal_step",
  CREDIBILITY = "credibility_step",
  SPANISH_LEVEL = "spanish_level_step",
  FLUENT_GOAL = "fluent_goal_step",
  STUDY_HABIT = "study_habit_step",
  NOTIFICATION = "notification_step",
  CHALLENGES = "challenges_step",
  PLAN_LOADING = "plan_loading_step",
  PLAN_LOADED = "plan_loaded_step",
  FREE_TRIAL = "free_trial_step",
}

// Step map for onboarding flow
export const OnboardingStepMap: Record<OnboardingAnalyticsKey, number> = {
  [OnboardingAnalyticsKey.LANGUAGE]: 0,
  [OnboardingAnalyticsKey.MOTIVATION_GOAL]: 1,
  [OnboardingAnalyticsKey.CREDIBILITY]: 2,
  [OnboardingAnalyticsKey.SPANISH_LEVEL]: 3,
  [OnboardingAnalyticsKey.FLUENT_GOAL]: 4,
  [OnboardingAnalyticsKey.STUDY_HABIT]: 5,
  [OnboardingAnalyticsKey.NOTIFICATION]: 6,
  [OnboardingAnalyticsKey.CHALLENGES]: 7,
  [OnboardingAnalyticsKey.PLAN_LOADING]: 8,
  [OnboardingAnalyticsKey.PLAN_LOADED]: 9,
  [OnboardingAnalyticsKey.FREE_TRIAL]: 10,
};

interface OnboardingPageProps {
  assetSources?: {
    parrotDemo: string;
    iphone: string;
  };
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ assetSources }) => {
  const nav = useNavigate();
  const { "*": stepParam } = useParams();
  const initialStep = stepParam ? parseInt(stepParam, 10) : 0;
  const [step, setStep] = useState(initialStep);
  const [language, setLanguage] = useState("Spanish");
  const [goal, setGoal] = useState("");
  const [fluent, setFluent] = useState(9);
  const [challenge, setChallenge] = useState("");
  const [dailyStudy, setDailyStudy] = useState(15);
  const [level, setLevel] = useState<LanguageLevelOnboarding | null>(null);
  const [showCheer, setShowCheer] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const PLATFORM = getPlatform();
  const posthog = usePostHog();
  const HAS_IOS_NATIVE_AUTH =
    PLATFORM === "apple" && window.webkit?.messageHandlers["socialAuth"];

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: async () => {
      const getAuthTokenWithRetry = async (
        retries = 10,
        delay = 1000
      ): Promise<string> => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          const token = tokenStorage.getToken();
          if (token) {
            return token;
          }
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay *= 2; // Exponential backoff
        }
        throw new Error(
          "Failed to retrieve auth token after multiple attempts"
        );
      };

      const token = await getAuthTokenWithRetry();
      // Use the token as needed, e.g., in API requests
      return getUserInfo({ authToken: token });
    },
  });

  const hasPushToken = userInfoQuery.data?.hasPushToken;

  const { updateOnboardingField, submitOnboarding } = useOnboarding();

  const handleSetLanguage = (language: string) => {
    setLanguage(language);
  };

  const handleSetGoal = (goalText: string, code: LearningReason) => {
    setGoal(goalText);
    updateOnboardingField("whyDoYouWantToLearn", code);
  };

  const handleSetFluent = (fluent: number) => {
    setFluent(fluent);
    updateOnboardingField("whenDoYouWantToBeFluentBy", fluent);
  };

  const handleSetDailyStudy = (dailyStudy: number) => {
    setDailyStudy(dailyStudy);
    updateOnboardingField("howMuchCanYouStudyEachDay", dailyStudy);
  };

  const handleSetChallenge = (
    challenge: string,
    code: LanguageLearningChallenge
  ) => {
    setChallenge(challenge);
    updateOnboardingField(
      "whatIsYourBiggestChallengeWithLearningLanguage",
      code
    );
  };

  const handleSetLevel = (code: LanguageLevelOnboarding) => {
    setLevel(code);
    updateOnboardingField("currentSpanishLevel", code);
  };

  const targetDate = format(addMonths(new Date(), fluent), "MMM yyyy");

  const viewRoutes = [
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.LANGUAGE],
      analyticsKey: OnboardingAnalyticsKey.LANGUAGE,
      component: (
        <LanguageStep language={language} setLanguage={handleSetLanguage} />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.MOTIVATION_GOAL],
      analyticsKey: OnboardingAnalyticsKey.MOTIVATION_GOAL,
      component: <MotivationGoal setGoal={handleSetGoal} startingGoal={goal} />,
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.CREDIBILITY],
      analyticsKey: OnboardingAnalyticsKey.CREDIBILITY,
      component: <Credibility />,
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.SPANISH_LEVEL],
      analyticsKey: OnboardingAnalyticsKey.SPANISH_LEVEL,
      component: (
        <SpanishLevelStep
          level={level as LanguageLevelOnboarding}
          setLevel={handleSetLevel}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.FLUENT_GOAL],
      analyticsKey: OnboardingAnalyticsKey.FLUENT_GOAL,
      component: (
        <FluentGoal
          fluent={fluent}
          setFluent={handleSetFluent}
          level={level as LanguageLevelOnboarding}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.STUDY_HABIT],
      analyticsKey: OnboardingAnalyticsKey.STUDY_HABIT,
      component: (
        <StudyHabit
          dailyStudy={dailyStudy}
          setDailyStudy={handleSetDailyStudy}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.NOTIFICATION],
      analyticsKey: OnboardingAnalyticsKey.NOTIFICATION,
      component: (
        <NotificationStep
          onClose={() => {
            setStep(step + 1);
            nav(`/onboarding/${step + 1}`);
          }}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.CHALLENGES],
      analyticsKey: OnboardingAnalyticsKey.CHALLENGES,
      component: <Challenges setChallenge={handleSetChallenge} />,
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.PLAN_LOADING],
      analyticsKey: OnboardingAnalyticsKey.PLAN_LOADING,
      component: (
        <PlanLoading
          onLoadingComplete={() => {
            setStep(step + 1);
            nav(`/onboarding/${step + 1}`);
          }}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.PLAN_LOADED],
      analyticsKey: OnboardingAnalyticsKey.PLAN_LOADED,
      component: (
        <PlanLoaded
          language={language}
          dailyStudy={dailyStudy}
          fluent={targetDate}
          challenge={challenge}
          goal={goal}
        />
      ),
    },
    {
      step: OnboardingStepMap[OnboardingAnalyticsKey.FREE_TRIAL],
      analyticsKey: OnboardingAnalyticsKey.FREE_TRIAL,
      component: <FreeTrial platform={PLATFORM} assetSources={assetSources} />,
    },
  ];
  const handleBack = () => {
    if (step === 10) return;
    document.documentElement.style.setProperty(
      "--slide-out-animation",
      "slide-out-horizontal-reverse"
    );
    document.documentElement.style.setProperty(
      "--slide-in-animation",
      "slide-in-horizontal-reverse"
    );
    const navigateToPrevious = () => {
      switch (step) {
        case 8:
          if (HAS_IOS_NATIVE_AUTH && !hasPushToken) {
            setStep(step - 1);
            nav(`/onboarding/${step - 1}`);
            return;
          } else {
            setStep(step - 2);
            nav(`/onboarding/${step - 2}`);
            return;
          }
        default:
          if (step > 0) {
            setStep(step - 1);
            nav(`/onboarding/${step - 1}`);
            if (step - 1 === getStep(OnboardingAnalyticsKey.FLUENT_GOAL)) {
              setShowCheer(false);
            }
          } else {
            nav("/");
          }
          break;
      }
    };
    // Check if startViewTransition is supported
    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(navigateToPrevious);
    } else {
      // Directly navigate without transition
      navigateToPrevious();
    }
  };

  const handleSubmitOnboarding = async () => {
    try {
      await submitOnboarding();
      return true;
    } catch (error) {
      console.error("Failed to submit onboarding data:", error);
      return false;
    }
  };

  const isPressed = useRef(false);
  const isTransitioning = useRef(false);

  const handleNext = async () => {
    if (isPressed.current || isTransitioning.current) return;
    isPressed.current = true;
    isTransitioning.current = true;

    setTimeout(() => {
      isPressed.current = false;
    }, 1000);

    // If we're on the FluentGoal step, show cheer message and delay navigation
    if (step === getStep(OnboardingAnalyticsKey.FLUENT_GOAL)) {
      setShowCheer(true);
      setTimeout(() => {
        // this checks if the ios device has the startViewTransition available
        if (typeof document.startViewTransition === "function") {
          document.startViewTransition(() => {
            document.documentElement.style.setProperty(
              "--slide-out-animation",
              "slide-out-horizontal"
            );
            document.documentElement.style.setProperty(
              "--slide-in-animation",
              "slide-in-horizontal"
            );
            setStep(step + 1);
            nav(`/onboarding/${step + 1}`);
            isTransitioning.current = false;
          });
        } else {
          document.documentElement.style.setProperty(
            "--slide-out-animation",
            "slide-out-horizontal"
          );
          document.documentElement.style.setProperty(
            "--slide-in-animation",
            "slide-in-horizontal"
          );
          setStep(step + 1);
          nav(`/onboarding/${step + 1}`);
          isTransitioning.current = false;
        }
      }, 2000);
      return;
    }

    document.documentElement.style.setProperty(
      "--slide-out-animation",
      "slide-out-horizontal"
    );
    document.documentElement.style.setProperty(
      "--slide-in-animation",
      "slide-in-horizontal"
    );

    // If we're at the last step (Free Trial), submit the onboarding data
    if (step === 11) {
      handleSubmitOnboarding();
    }

    const navigateToNext = () => {
      switch (step) {
        case getStep(OnboardingAnalyticsKey.NOTIFICATION) - 1:
          if (HAS_IOS_NATIVE_AUTH && !hasPushToken) {
            setStep(step + 1);
            nav(`/onboarding/${step + 1}`);
            isTransitioning.current = false;
            return;
          } else {
            setStep(step + 2);
            nav(`/onboarding/${step + 2}`);
            isTransitioning.current = false;
            return;
          }
        case 11:
          if (HAS_IOS_NATIVE_AUTH && !hasPushToken) {
            onOpen();
            isTransitioning.current = false;
            return;
          } else {
            nav("/premium");
            isTransitioning.current = false;
            return;
          }
        default:
          if (step < totalSteps - 1) {
            setStep(step + 1);
            nav(`/onboarding/${step + 1}`);
            isTransitioning.current = false;
          } else {
            nav("/premium");
            isTransitioning.current = false;
          }
          break;
      }
    };
    // Check if startViewTransition is supported
    if (typeof document.startViewTransition === "function") {
      document.startViewTransition(navigateToNext);
    } else {
      // Directly navigate without transition
      navigateToNext();
    }
  };

  // the safe default is the plan loaded?
  const getStep = (analyticsKey: OnboardingAnalyticsKey) => {
    return OnboardingStepMap[analyticsKey];
  };

  // disable for free users avoid infinite redirect loop.
  const isBackHidden = userInfoQuery.data?.isFree && step === 0;

  useEffect(() => {
    const key = viewRoutes[step]?.analyticsKey;
    if (key) {
      posthog.capture(`onboarding_step_${key}`);
    }
  }, [step, posthog]);

  const totalSteps = viewRoutes.length;
  return (
    <div
      className={`appPadding flex flex-col h-full w-full pt-0 ${
        PLATFORM === "web" ? "bottom-padding-web" : "bottom-padding"
      }`}
    >
      {step !== 9 && step !== 11 && (
        <OnboardingHeader
          step={step}
          totalSteps={totalSteps}
          handleBack={handleBack}
          hideBack={isBackHidden}
        />
      )}
      <div className="flex flex-grow w-full h-full overflow-auto">
        <Routes>
          {/* Redirect from base URL to the first step */}
          <Route path="" element={<Navigate to={`${step}`} />} />
          {/* Dynamically render routes for each step */}
          {viewRoutes.map(({ step, component }) => (
            <Route key={step} path={`${step}`} element={component} />
          ))}
        </Routes>
      </div>
      {step === getStep(OnboardingAnalyticsKey.FLUENT_GOAL) ? (
        <div className="flex justify-center mb-8">
          <div
            className={`inline-block px-4 py-2 rounded-full transition-all duration-300 ease-in-out ${
              showCheer
                ? "bg-[var(--color-green)] text-white animate-pulse-once"
                : "bg-[#DBEAFE] text-[var(--color-blue)]"
            }`}
          >
            {showCheer
              ? "🎉 Parrot can help you get there!"
              : `🇪🇸 Fluent by ${targetDate}!`}
          </div>
        </div>
      ) : null}
      {step !== getStep(OnboardingAnalyticsKey.NOTIFICATION) &&
        step !== getStep(OnboardingAnalyticsKey.PLAN_LOADING) && (
          <ContinueButton
            text={
              step === getStep(OnboardingAnalyticsKey.STUDY_HABIT)
                ? "I'm committed"
                : step === getStep(OnboardingAnalyticsKey.CHALLENGES)
                ? "Build my plan"
                : step === getStep(OnboardingAnalyticsKey.FREE_TRIAL)
                ? "Try for $0.00"
                : "Continue"
            }
            onClick={handleNext}
            disabled={
              isTransitioning.current ||
              (step === getStep(OnboardingAnalyticsKey.LANGUAGE) &&
                !language) ||
              (step === getStep(OnboardingAnalyticsKey.MOTIVATION_GOAL) &&
                goal == "") ||
              (step === getStep(OnboardingAnalyticsKey.SPANISH_LEVEL) &&
                level == null) ||
              (step === getStep(OnboardingAnalyticsKey.CHALLENGES) &&
                challenge == "")
            }
          />
        )}
      {isOpen && (
        <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
          <ModalOverlay />
          <ModalContent
            bg={"transparent"}
            justifyContent={"center"}
            alignItems={"center"}
            boxShadow={"none"}
          >
            <NotificationPopUp
              onClose={() => {
                onClose();
                nav("/premium");
              }}
            />
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default OnboardingPage;
